import type { DebugLogDto } from "@babackapp/private-api-client/client/use-get-debug-logs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { CodeIcon } from "@radix-ui/react-icons";
import ActivityWrapper from "./activity-wrapper";
import ActivityIconWrapper from "./activity-icon-wrapper";
import ActivityContentWrapper from "./activity-content-wrapper";
import { ActivityItemHeader } from "./activity-item-header";
import { Badge } from "@babackapp/ui/components/badge";

type DebugLogActivityProps = {
  debugLog: DebugLogDto;
};

export default function DebugLogActivity({ debugLog }: DebugLogActivityProps) {
  const isError = debugLog.status === "error";

  return (
    <ActivityWrapper>
      <ActivityIconWrapper
        className={isError ? "text-red-500 bg-red-100" : "text-blue-500 bg-blue-100"}
      >
        <CodeIcon className="size-4" />
      </ActivityIconWrapper>
      <ActivityContentWrapper>
        <div className="flex items-center gap-2 flex-1 justify-between w-full min-w-0">
          <Accordion
            type="single"
            collapsible
            className="flex-1 min-w-0 w-full"
          >
            <AccordionItem value="item-1" className="border-0">
              <AccordionTrigger className="p-0 w-full min-w-0">
                <ActivityItemHeader
                  title=""
                  timestamp={debugLog.createdAt}
                  description={
                    debugLog.error ? (
                      <span className="text-xs text-red-600 font-light truncate block max-w-[500px]">
                        {debugLog.error}
                      </span>
                    ) : undefined
                  }
                >
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={isError ? "destructive" : "secondary"}
                      className="text-[10px] px-1.5 py-0"
                    >
                      {debugLog.source}
                    </Badge>
                    <span>{debugLog.operation}</span>
                    <span className="text-muted-foreground text-[10px]">
                      {debugLog.operationType}
                    </span>
                    {debugLog.duration != null && (
                      <span className="text-muted-foreground text-[10px]">
                        {debugLog.duration}ms
                      </span>
                    )}
                  </div>
                </ActivityItemHeader>
              </AccordionTrigger>
              <AccordionContent>
                {debugLog.request && (
                  <div className="mt-2">
                    <span className="text-xs font-medium text-muted-foreground">
                      Request
                    </span>
                    <pre className="mt-1 rounded-md bg-muted p-3 text-xs overflow-x-auto whitespace-pre-wrap max-h-60 overflow-y-auto">
                      {JSON.stringify(debugLog.request, null, 2)}
                    </pre>
                  </div>
                )}
                {debugLog.response && (
                  <div className="mt-2">
                    <span className="text-xs font-medium text-muted-foreground">
                      Response
                    </span>
                    <pre className="mt-1 rounded-md bg-muted p-3 text-xs overflow-x-auto whitespace-pre-wrap max-h-60 overflow-y-auto">
                      {JSON.stringify(debugLog.response, null, 2)}
                    </pre>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </ActivityContentWrapper>
    </ActivityWrapper>
  );
}
